import React, { useState, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import io from 'socket.io-client';
import { API_URL, apiGet, apiPatch, apiPost } from '../api';
import { formatNumberPrecise } from '../Utils';
import DailyHistoryViewer from './TeenPattiame/DailyHistoryViewer';
import './Admin.css';

// Game runs in zoneserver (/roulette namespace); settings/stats via /api/games.
const GAME_KEY = 'roulette';
const SOCKET_SERVER_URL = `${API_URL}/${GAME_KEY}`;

const BIAS_OPTIONS = [-30, -15, -10, -5, 0, 5, 10, 15, 20];

const SECTION_COLORS = {
    red:   '#D92636',
    black: '#9B9BB5',
    green: '#00C853',
};

const RouletteAdmin = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isClockRunning, setIsClockRunning] = useState(false);
    const [gamePhase, setGamePhase] = useState('N/A');
    const [roundId, setRoundId] = useState(0);
    const [countdown, setCountdown] = useState(0);
    const [phaseStartTime, setPhaseStartTime] = useState(null);
    const [phaseDuration, setPhaseDuration] = useState(0);
    const [appStats, setAppStats] = useState({ totalProfit: 0 });
    const [winningHistory, setWinningHistory] = useState([]);
    const [lastWinnerInfo, setLastWinnerInfo] = useState(null);
    const [pots, setPots] = useState({ red: 0, black: 0, green: 0 });
    const [selectedBias, setSelectedBias] = useState(0);
    const [sectionLimit, setSectionLimit] = useState(100000);
    const [loading, setLoading] = useState(false);
    const [biasLoading, setBiasLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const socketRef = useRef(null);
    const animationFrameId = useRef(null);

    useEffect(() => {
        let isMounted = true;

        const initAdminSocket = async () => {
            try {
                const auth = getAuth();
                const token = await auth.currentUser?.getIdToken(true);
                if (!token || !isMounted) return;
                if (socketRef.current) socketRef.current.disconnect();
                socketRef.current = io(SOCKET_SERVER_URL, {
                    transports: ['websocket', 'polling'],
                    reconnection: true,
                    reconnectionAttempts: Infinity,
                    reconnectionDelay: 1000,
                    auth: { token },
                });
                socketRef.current.on('connect', () => console.log('Roulette socket connected'));
                socketRef.current.on('disconnect', (reason) => console.warn('Roulette socket disconnected:', reason));
                socketRef.current.on('gameStateUpdate', (data) => {
                    if (!isMounted) return;
                    if (data.gamePhase) setGamePhase(data.gamePhase);
                    if (typeof data.roundId === 'number') setRoundId(data.roundId);
                    if (data.pots) setPots(data.pots);
                    if (data.timerInfo) { setPhaseStartTime(data.timerInfo.phaseStartTime); setPhaseDuration(data.timerInfo.phaseDuration); }
                    if (data.lastWinnerInfo !== undefined) setLastWinnerInfo(data.lastWinnerInfo);
                    if (data.winningHistory !== undefined) setWinningHistory(data.winningHistory);
                });
                socketRef.current.on('betUpdate', (data) => { if (!isMounted) return; if (data.pots) setPots(data.pots); });
                socketRef.current.on('potsUpdate', (data) => { if (!isMounted) return; if (data.pots) setPots(data.pots); });
            } catch (error) { console.error('Error initializing roulette socket:', error); }
        };

        initAdminSocket();

        const handleOnline = () => { setIsOnline(true); initAdminSocket(); };
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Settings + house stats: poll the backend (was adminSettings/appStats
        // onSnapshot). winningHistory + lastWinnerInfo arrive via the socket above.
        let settingsSeeded = false;
        const loadAdminData = async () => {
            try {
                const [settings, statsRes] = await Promise.all([
                    apiGet(`/api/games/${GAME_KEY}/settings`),
                    apiGet(`/api/games/${GAME_KEY}/stats`),
                ]);
                if (!isMounted) return;
                if (settings) {
                    setIsClockRunning(settings.isClockRunning ?? false);
                    if (!settingsSeeded) {
                        setSelectedBias(settings.biasPercentage ?? 0);
                        setSectionLimit(settings.sectionLimit || 100000);
                        settingsSeeded = true;
                    }
                }
                if (statsRes?.stats) setAppStats(statsRes.stats);
            } catch (e) { /* transient — keep last known values */ }
        };
        loadAdminData();
        const adminPoll = setInterval(loadAdminData, 3000);

        return () => {
            isMounted = false;
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            if (socketRef.current) socketRef.current.disconnect();
            if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
            clearInterval(adminPoll);
        };
    }, []);

    useEffect(() => {
        if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
        if (!isClockRunning || !phaseStartTime || !phaseDuration || !isOnline) { setCountdown(0); return; }
        const phaseEndTime = phaseStartTime + (phaseDuration * 1000);
        const tick = () => {
            const now = Date.now();
            const left = Math.max(0, phaseEndTime - now);
            setCountdown(Math.ceil(left / 1000));
            if (left > 0) animationFrameId.current = requestAnimationFrame(tick);
        };
        animationFrameId.current = requestAnimationFrame(tick);
        return () => { if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current); };
    }, [isClockRunning, phaseStartTime, phaseDuration, isOnline]);

    const handleToggleClock = async () => {
        setLoading(true);
        try { await apiPatch(`/api/games/${GAME_KEY}/settings`, { isClockRunning: !isClockRunning }); } catch (err) { console.error(err); }
        setLoading(false);
    };

    const handleSaveSettings = async (e) => {
        e.preventDefault(); setBiasLoading(true);
        try {
            await apiPatch(`/api/games/${GAME_KEY}/settings`, { sectionLimit: Number(sectionLimit), biasPercentage: selectedBias });
            alert('Roulette Settings Applied!');
        } catch (err) { alert('Error saving settings'); }
        setBiasLoading(false);
    };

    const handleResetRoundId = async () => {
        if (!window.confirm('Round ID 0 করতে চান? winningHistory আর last winner reset হবে। Undo করা যাবে না।')) return;
        try {
            await apiPost(`/api/games/${GAME_KEY}/reset-round`);
            alert('Round ID reset triggered.');
        } catch (err) { alert('Reset failed: ' + err.message); }
    };

    const handleClearHistory = async () => {
        if (!window.confirm('সব পুরনো dailyGameHistory_Roulette delete হবে। নিশ্চিত?')) return;
        try {
            await apiPost(`/api/games/${GAME_KEY}/clear-history`);
            alert('Clear History triggered.');
        } catch (err) { alert('Clear failed: ' + err.message); }
    };

    const getPhaseColor = (phase) => ({ betting: '#3b82f6', pause: '#6b7280', spinning: '#a78bfa', victory: '#f59e0b', showdown: '#f59e0b', nextRound: '#6366f1' }[phase] || '#6b7280');
    const getSectionColor = (section) => SECTION_COLORS[section?.toLowerCase()] || '#6b7280';

    return (
        <div className="dark-page" style={{ position: 'relative' }}>
            {/* Offline Banner */}
            {!isOnline && (
                <div style={{ background: '#dc2626', color: '#fff', textAlign: 'center', padding: '10px', fontWeight: 700, borderRadius: 8, marginBottom: 12 }}>
                    ⚠️ No Internet Connection. Game updates paused.
                </div>
            )}

            {/* Header */}
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>FY Roulette Control 🎡</h2>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: isOnline ? 'rgba(59,130,246,0.25)' : 'rgba(107,114,128,0.25)', color: isOnline ? '#60a5fa' : '#9ca3af', border: `1px solid ${isOnline ? 'rgba(59,130,246,0.4)' : 'rgba(107,114,128,0.4)'}` }}>
                        {isOnline ? 'ONLINE' : 'OFFLINE'}
                    </span>
                    <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: isClockRunning ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)', color: isClockRunning ? '#4ade80' : '#f87171', border: `1px solid ${isClockRunning ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}` }}>
                        {isClockRunning ? 'RUNNING' : 'STOPPED'}
                    </span>
                </div>
            </div>

            {/* Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
                {[
                    { label: 'CURRENT PHASE', value: gamePhase.toUpperCase(), color: getPhaseColor(gamePhase) },
                    { label: 'TIMER', value: `${countdown}s`, color: '#f59e0b' },
                    { label: 'ROUND ID', value: roundId, color: '#a78bfa' },
                    { label: 'TOTAL PROFIT', value: formatNumberPrecise(appStats.totalProfit), color: '#4ade80' },
                ].map((s, i) => (
                    <div key={i} className="dark-card" style={{ borderTop: `3px solid ${s.color}`, textAlign: 'center' }}>
                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 700, marginBottom: 8 }}>{s.label}</p>
                        <p style={{ fontSize: i === 1 ? 28 : 22, fontWeight: 900, color: s.color, margin: 0, fontFamily: 'monospace' }}>{s.value}</p>
                    </div>
                ))}
            </div>

            {/* Live Pots */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                {['red', 'black', 'green'].map(section => (
                    <div key={section} className="dark-card" style={{ borderTop: `3px solid ${getSectionColor(section)}`, textAlign: 'center' }}>
                        <p style={{ fontSize: 18, fontWeight: 900, color: getSectionColor(section), marginBottom: 4, textTransform: 'uppercase' }}>
                            {section === 'red' ? '🔴' : section === 'black' ? '⚫' : '🟢'} {section}
                        </p>
                        <p style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#f1f5f9' }}>{formatNumberPrecise(pots?.[section] || 0)}</p>
                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', margin: '4px 0 0', fontWeight: 600 }}>{section === 'green' ? '14× payout' : '2× payout'}</p>
                    </div>
                ))}
            </div>

            {/* Settings */}
            <div className="dark-card" style={{ marginBottom: 16 }}>
                <h3 style={{ textAlign: 'center', marginBottom: 20, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>Engine Parameters</h3>
                <form onSubmit={handleSaveSettings}>
                    <div style={{ maxWidth: 260, margin: '0 auto 20px', textAlign: 'center' }}>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'rgba(255,255,255,0.7)' }}>Section Payout Limit</label>
                        <input type="number" value={sectionLimit} onChange={e => setSectionLimit(e.target.value)} disabled={!isOnline} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#f1f5f9', fontSize: 18, textAlign: 'center', fontWeight: 700 }} />
                    </div>
                    <div style={{ textAlign: 'center', marginBottom: 20 }}>
                        <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'rgba(255,255,255,0.7)' }}>Algorithm Bias (Winning Control)</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
                            {BIAS_OPTIONS.map(val => (
                                <button key={val} type="button" onClick={() => setSelectedBias(val)} disabled={!isOnline} style={{
                                    padding: '8px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                                    background: selectedBias === val ? 'linear-gradient(135deg,#ff4d6d,#ff2d55)' : 'rgba(255,255,255,0.07)',
                                    color: selectedBias === val ? '#fff' : 'rgba(255,255,255,0.6)',
                                    border: selectedBias === val ? 'none' : '1px solid rgba(255,255,255,0.12)',
                                    cursor: !isOnline ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
                                }}>
                                    {val > 0 ? `+${val}` : val}%
                                </button>
                            ))}
                        </div>
                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>
                            <b>Minus (-):</b> Profit Mode &nbsp;|&nbsp; <b>Zero (0):</b> Random &nbsp;|&nbsp; <b>Plus (+):</b> Give Bonus
                        </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <button type="submit" disabled={biasLoading || !isOnline} className="btn-upload" style={{ padding: '12px 40px', opacity: (biasLoading || !isOnline) ? 0.5 : 1 }}>
                            {biasLoading ? 'Saving...' : 'APPLY SETTINGS'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Winning History */}
            {winningHistory.length > 0 && (
                <div className="dark-card" style={{ marginBottom: 16 }}>
                    <h3 style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'rgba(255,255,255,0.5)' }}>RECENT HISTORY</h3>
                    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 8 }}>
                        {winningHistory.slice(0, 14).map((entry, idx) => (
                            <div key={idx} style={{
                                width: 40, height: 40, borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 900, fontSize: 13,
                                background: getSectionColor(entry.winnerSection || entry.section),
                                color: '#fff',
                                boxShadow: `0 2px 8px ${getSectionColor(entry.winnerSection || entry.section)}60`,
                                border: '2px solid rgba(255,255,255,0.15)',
                            }}>
                                {entry.winnerNumber ?? entry.number ?? '?'}
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 12 }}>
                        {['red', 'black', 'green'].map(s => (
                            <span key={s} style={{ fontSize: 11, color: getSectionColor(s), fontWeight: 700 }}>
                                ● {s.toUpperCase()} {winningHistory.filter(e => (e.winnerSection || e.section) === s).length}x
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Last Winner */}
            {lastWinnerInfo && (
                <div className="dark-card" style={{ marginBottom: 16, textAlign: 'center', borderTop: `3px solid ${getSectionColor(lastWinnerInfo.winnerSection)}` }}>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 700 }}>LAST WINNER</p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 8 }}>
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: getSectionColor(lastWinnerInfo.winnerSection), display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 20, color: '#fff' }}>
                            {lastWinnerInfo.winnerNumber}
                        </div>
                        <div>
                            <p style={{ fontSize: 22, fontWeight: 900, color: getSectionColor(lastWinnerInfo.winnerSection), margin: 0, textTransform: 'uppercase' }}>
                                {lastWinnerInfo.winnerSection}
                            </p>
                            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', margin: '4px 0 0' }}>
                                {lastWinnerInfo.winnerSection === 'green' ? '14× payout' : '2× payout'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Control Buttons */}
            <div style={{ marginBottom: 12 }}>
                <button onClick={handleToggleClock} disabled={loading || !isOnline} style={{
                    width: '100%', padding: '16px', borderRadius: 12, fontWeight: 900, fontSize: 16, color: '#fff', border: 'none',
                    cursor: (loading || !isOnline) ? 'not-allowed' : 'pointer',
                    background: isClockRunning ? 'linear-gradient(135deg,#ef4444,#dc2626)' : 'linear-gradient(135deg,#22c55e,#16a34a)',
                    boxShadow: isClockRunning ? '0 4px 20px rgba(239,68,68,0.4)' : '0 4px 20px rgba(34,197,94,0.4)',
                    opacity: (loading || !isOnline) ? 0.5 : 1, transition: 'all 0.3s',
                }}>
                    {loading ? 'Processing...' : (isClockRunning ? 'STOP GAME SERVER' : 'START GAME SERVER')}
                </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
                <button onClick={handleResetRoundId} disabled={!isOnline} style={{ padding: '12px', borderRadius: 10, fontWeight: 700, color: '#fff', border: 'none', background: 'linear-gradient(135deg,#f97316,#ea580c)', cursor: !isOnline ? 'not-allowed' : 'pointer', opacity: !isOnline ? 0.5 : 1 }}>
                    RESET ROUND ID
                </button>
                <button onClick={handleClearHistory} disabled={!isOnline} style={{ padding: '12px', borderRadius: 10, fontWeight: 700, color: '#fff', border: 'none', background: 'linear-gradient(135deg,#e11d48,#be123c)', cursor: !isOnline ? 'not-allowed' : 'pointer', opacity: !isOnline ? 0.5 : 1 }}>
                    CLEAR ALL HISTORY
                </button>
            </div>

            {/* Daily History */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
                <button onClick={() => setShowHistory(!showHistory)} style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, fontWeight: 700, color: '#f1f5f9', cursor: 'pointer', fontSize: 14, transition: 'all 0.2s' }}>
                    {showHistory ? 'HIDE DETAILED HISTORY' : 'SHOW DAILY DETAILED HISTORY'}
                </button>
                {showHistory && <div style={{ marginTop: 16 }}><DailyHistoryViewer gameType="roulette" /></div>}
            </div>
        </div>
    );
};

export default RouletteAdmin;
